import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Users, Clock, Tag, Check, Loader2, ShieldCheck, Trophy, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const step = (n, label, active, done) => (
  <div className="flex items-center gap-2">
    <div className={`h-7 w-7 rounded-full grid place-items-center text-xs font-bold ${done ? "bg-emerald-500 text-white" : active ? "bg-[#F9CF04] text-[#1a0a0a]" : "bg-white/10 text-slate-400"}`}>
      {done ? <Check className="h-4 w-4" /> : n}
    </div>
    <span className={`text-sm ${active || done ? "text-white" : "text-slate-500"}`}>{label}</span>
  </div>
);

export default function Booking() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [locId, setLocId] = useState(null);
  const [dates, setDates] = useState([]);
  const [dateSel, setDateSel] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [players, setPlayers] = useState(3);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "" });
  const [coupon, setCoupon] = useState("");
  const [priceInfo, setPriceInfo] = useState(null);
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cfg, setCfg] = useState({ razorpay_enabled: false });
  const [calMonth, setCalMonth] = useState(null);

  useEffect(() => {
    api.get(`/games/${gameId}`).then((r) => { setGame(r.data); setPlayers(r.data.min_players); }).catch(() => {});
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
  }, [gameId]);

  useEffect(() => {
    if (game && locId) api.get(`/games/${gameId}/dates`, { params: { location_id: locId } }).then((r) => {
      setDates(r.data);
      setDateSel(null); setSlot(null); setPriceInfo(null);
      if (r.data[0]) { const [y, m] = r.data[0].split("-").map(Number); setCalMonth(new Date(y, m - 1, 1)); }
    });
  }, [locId]);

  useEffect(() => {
    if (locId && dateSel) api.get("/slots", { params: { game_id: gameId, location_id: locId, date: dateSel } }).then((r) => setSlots(r.data));
  }, [dateSel]);

  const perPersonFor = (s, n) => {
    if (s.price_override) return s.price_override;
    const t = (s.price_tiers || []).find((x) => n >= x.min_players && n <= x.max_players && x.per_person);
    if (t) return t.per_person;
    return s.fallback_price ?? s.price ?? 0;
  };

  useEffect(() => {
    if (slot) {
      const pp = perPersonFor(slot, players);
      const base = pp * players;
      const disc = Math.round(base * (slot.discount_percent || 0)) / 100;
      setPriceInfo({ base, discount: disc, total: base - disc, per_person: pp });
    }
  }, [slot, players]);

  const selectSlot = (s) => {
    if (slot?.id === s.id) { setSlot(null); setPriceInfo(null); return; }
    setSlot(s);
    setCoupon("");
    setPlayers(s.min_players);
    const pp = perPersonFor(s, s.min_players);
    const base = pp * s.min_players;
    const disc = Math.round(base * (s.discount_percent || 0)) / 100;
    setPriceInfo({ base, discount: disc, total: base - disc, per_person: pp });
  };

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, slot_id: slot.id, num_players: players });
      setPriceInfo({ base: data.base, discount: data.discount, total: data.total, per_person: perPersonFor(slot, players) });
      toast.success(`Coupon applied · you save ₹${data.discount}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const loadRazorpay = () => new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true); s.onerror = () => res(false);
    document.body.appendChild(s);
  });

  const submit = async (payOption) => {
    if (!terms) return toast.error("Please accept the terms & conditions");
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        slot_id: slot.id, ...form, num_players: players,
        coupon_code: coupon || null, terms_accepted: terms, payment_option: payOption,
      });
      if (data.razorpay) {
        const ok = await loadRazorpay();
        if (!ok) { toast.error("Could not load payment gateway"); setSubmitting(false); return; }
        const rz = new window.Razorpay({
          key: data.razorpay.key_id, amount: data.razorpay.amount, currency: "INR",
          name: "Escape Room Express", description: game.name, order_id: data.razorpay.order_id,
          prefill: { name: form.customer_name, email: form.customer_email, contact: form.customer_phone },
          theme: { color: "#F9CF04" },
          handler: async (resp) => {
            try {
              await api.post("/payments/verify", {
                booking_ref: data.booking.booking_ref,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              navigate(`/booking/${data.booking.booking_ref}`);
            } catch (e) { toast.error(errMsg(e)); }
          },
        });
        rz.open();
        setSubmitting(false);
      } else {
        navigate(`/booking/${data.booking.booking_ref}`);
      }
    } catch (e) { toast.error(errMsg(e)); setSubmitting(false); }
  };

  if (!game) return <div className="max-w-7xl mx-auto px-5 py-20 text-slate-400"><Loader2 className="animate-spin" /></div>;
  const canBook = form.customer_name && form.customer_email && form.customer_phone && terms;

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <div className="flex flex-wrap gap-5 mb-8">
        {step(1, "Location", !locId, !!locId)}
        {step(2, "Slot", locId && !slot, !!slot)}
        {step(3, "Details & Pay", !!slot, false)}
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Left: game summary */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#241211] sticky top-24">
            {game.image_url ? <img src={game.image_url} alt={game.name} className="w-full h-48 object-cover" /> : <div className="w-full h-48 bg-gradient-to-br from-[#3a1512] to-[#1a0a0a]" />}
            <div className="p-5">
              <h1 className="font-display font-bold text-2xl">{game.name}</h1>
              <p className="text-xs font-mono uppercase tracking-wider text-[#F9CF04] mt-1">{game.theme}</p>
              <p className="text-sm text-slate-400 mt-3">{game.description}</p>
              <div className="mt-4 flex gap-4 text-xs text-slate-300">
                <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-[#F9CF04]" /> {game.min_players}-{game.max_players}</span>
                <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-emerald-400" /> {game.duration_min}m</span>
              </div>
              {slot && priceInfo && (
                <div className="mt-5 rounded-xl bg-slate-950/50 border border-white/10 p-4 text-sm space-y-1" data-testid="price-summary">
                  <div className="flex justify-between text-slate-400"><span data-testid="price-per-person-line">₹{priceInfo.per_person ?? slot.price} × {players} players</span><span>₹{priceInfo.base}</span></div>
                  {priceInfo.discount > 0 && <div className="flex justify-between text-emerald-400"><span>Discount</span><span data-testid="price-discount">-₹{priceInfo.discount}</span></div>}
                  <div className="flex justify-between font-bold text-lg pt-1 border-t border-white/10"><span>Total</span><span className="font-mono" data-testid="price-total">₹{priceInfo.total}</span></div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: steps */}
        <div className="lg:col-span-3 space-y-6">
          {/* Location */}
          <section className="rounded-2xl border border-white/10 bg-[#241211] p-5">
            <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2"><MapPin className="h-5 w-5 text-[#F9CF04]" /> Where is the game?</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {game.locations.map((l) => (
                <button key={l.id} data-testid={`loc-${l.id}`} onClick={() => { setLocId(l.id); setSlot(null); setDateSel(null); setPriceInfo(null); }}
                  className={`text-left rounded-xl border p-4 transition-all ${locId === l.id ? "border-[#F9CF04] bg-[#F9CF04]/10" : "border-white/10 hover:border-white/25"}`}>
                  <div className="font-semibold">{l.name}</div>
                  <div className="text-xs text-slate-400 mt-1">{l.address}</div>
                </button>
              ))}
              {game.locations.length === 0 && <p className="text-slate-400 text-sm">No scheduled locations yet.</p>}
            </div>
          </section>

          {/* Slots */}
          {locId && (
            <section className="rounded-2xl border border-white/10 bg-[#241211] p-5">
              <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2"><Clock className="h-5 w-5 text-emerald-400" /> Pick a date & slot</h3>
              {dates.length === 0 && <p className="text-slate-400 text-sm">No available dates.</p>}
              {calMonth && dates.length > 0 && (() => {
                const avail = new Set(dates);
                const year = calMonth.getFullYear(), month = calMonth.getMonth();
                const firstDow = new Date(year, month, 1).getDay();
                const daysIn = new Date(year, month + 1, 0).getDate();
                const pad = (n) => String(n).padStart(2, "0");
                const cells = [];
                for (let i = 0; i < firstDow; i++) cells.push(null);
                for (let d = 1; d <= daysIn; d++) cells.push(`${year}-${pad(month + 1)}-${pad(d)}`);
                const monthHasAvail = dates.some((d) => { const [y, m] = d.split("-").map(Number); return y === year && m - 1 === month; });
                return (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <button data-testid="cal-prev" onClick={() => setCalMonth(new Date(year, month - 1, 1))}
                        className="h-8 w-8 grid place-items-center rounded-lg hover:bg-white/10 text-slate-300"><ChevronLeft className="h-4 w-4" /></button>
                      <div className="font-display font-semibold">{calMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</div>
                      <button data-testid="cal-next" onClick={() => setCalMonth(new Date(year, month + 1, 1))}
                        className="h-8 w-8 grid place-items-center rounded-lg hover:bg-white/10 text-slate-300"><ChevronRight className="h-4 w-4" /></button>
                    </div>
                    <div className="grid grid-cols-7 gap-1.5 text-center">
                      {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((w) => (
                        <div key={w} className="text-[11px] font-mono uppercase text-slate-500 pb-1">{w}</div>
                      ))}
                      {cells.map((d, i) => {
                        if (!d) return <div key={`b${i}`} />;
                        const isAvail = avail.has(d);
                        const isSel = dateSel === d;
                        const day = Number(d.split("-")[2]);
                        return (
                          <button key={d} data-testid={isAvail ? `date-${d}` : undefined} disabled={!isAvail}
                            onClick={() => { setDateSel(d); setSlot(null); setPriceInfo(null); setSlots([]); }}
                            className={`aspect-square rounded-lg text-sm font-medium transition-all ${
                              isSel ? "bg-[#F9CF04] text-[#1a0a0a] ring-2 ring-[#F9CF04]"
                              : isAvail ? "bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 border border-emerald-500/30"
                              : "text-slate-600 cursor-not-allowed"}`}>
                            {day}
                          </button>
                        );
                      })}
                    </div>
                    {!monthHasAvail && <p className="text-xs text-slate-500 mt-3 text-center">No open dates this month — use the arrows to browse.</p>}
                  </div>
                );
              })()}

              {dateSel && (
                <div className="mt-5 pt-5 border-t border-white/10 fade-up">
                  <div className="text-sm text-slate-400 mb-3">Slots for <span className="text-white font-medium">{new Date(dateSel + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</span></div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {slots.map((s) => (
                      <button key={s.id} data-testid={`slot-${s.id}`} onClick={() => selectSlot(s)}
                        className={`relative rounded-lg py-2.5 text-sm font-mono border transition-all ${slot?.id === s.id ? "bg-[#F9CF04] border-[#F9CF04] text-[#1a0a0a]" : s.is_challenge_slot ? "border-amber-500/50 text-amber-300 hover:bg-amber-500/10" : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"}`}>
                        {s.start_time}
                        {s.is_challenge_slot && <Trophy className="h-3 w-3 text-amber-400 absolute top-1 right-1" />}
                      </button>
                    ))}
                    {slots.length === 0 && <p className="text-slate-400 text-sm col-span-full py-2">No open slots this day.</p>}
                  </div>
                  {slot?.is_challenge_slot && (
                    <div className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 flex items-center gap-3 text-sm" data-testid="sponsored-banner">
                      {slot.sponsor_logo
                        ? <span className="bg-white/90 rounded-md px-2 py-1 shrink-0"><img src={slot.sponsor_logo} alt={slot.sponsor} className="h-6 w-auto object-contain" data-testid="sponsored-logo" /></span>
                        : <Trophy className="h-4 w-4 text-amber-400 shrink-0" />}
                      <span className="text-amber-200">
                        <b>Sponsored Challenge{slot.sponsor ? ` · ${slot.sponsor}` : ""}</b>
                        {slot.prize ? <> — top team wins <b>{slot.prize}</b></> : ""}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Details */}
          {slot && (
            <section className="rounded-2xl border border-white/10 bg-[#241211] p-5 fade-up">
              <h3 className="font-display font-semibold text-lg mb-4 flex items-center gap-2"><Users className="h-5 w-5 text-[#F9CF04]" /> Your team</h3>
              <div className="mb-4">
                <label className="text-sm text-slate-400">Number of players</label>
                <div className="flex gap-2 mt-2">
                  {Array.from({ length: slot.max_players - slot.min_players + 1 }, (_, i) => slot.min_players + i).map((n) => (
                    <button key={n} data-testid={`players-${n}`} onClick={() => { setPlayers(n); if (coupon) { setCoupon(""); toast.info("Re-apply your coupon after changing team size"); } }}
                      className={`h-11 w-11 rounded-lg font-bold border transition-all ${players === n ? "bg-[#F9CF04] border-[#F9CF04] text-[#1a0a0a]" : "border-white/10 hover:border-white/30"}`}>{n}</button>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input data-testid="input-name" placeholder="Full name" value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="bg-slate-950/50 border-white/10" />
                <Input data-testid="input-phone" placeholder="Phone" value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="bg-slate-950/50 border-white/10" />
                <Input data-testid="input-email" placeholder="Email" type="email" value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="bg-slate-950/50 border-white/10 sm:col-span-2" />
              </div>
              <div className="flex gap-2 mt-3">
                <Input data-testid="input-coupon" placeholder="Coupon code (optional)" value={coupon}
                  onChange={(e) => setCoupon(e.target.value.toUpperCase())} className="bg-slate-950/50 border-white/10" />
                <Button data-testid="apply-coupon-btn" variant="secondary" onClick={applyCoupon}><Tag className="h-4 w-4 mr-1" /> Apply</Button>
              </div>
              <label className="flex items-start gap-2 mt-4 text-sm text-slate-300 cursor-pointer">
                <Checkbox data-testid="terms-checkbox" checked={terms} onCheckedChange={setTerms} className="mt-0.5" />
                <span>I accept the <span className="text-[#F9CF04] underline">terms & conditions</span> — arrive 10 min early, non-refundable within 24h.</span>
              </label>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                {cfg.razorpay_enabled ? (
                  <>
                    <Button data-testid="pay-full-btn" disabled={!canBook || submitting} onClick={() => submit("full")}
                      className="bg-[#F9CF04] hover:bg-[#ffdb2e] text-[#1a0a0a] font-semibold flex-1">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Pay full ₹{priceInfo?.total}
                    </Button>
                    {game?.allow_partial !== false && (
                      <Button data-testid="pay-partial-btn" disabled={!canBook || submitting} onClick={() => submit("partial")} variant="secondary" className="flex-1">
                        Pay 25% now
                      </Button>
                    )}
                  </>
                ) : (
                  <Button data-testid="reserve-venue-btn" disabled={!canBook || submitting} onClick={() => submit("venue")}
                    className="bg-[#F9CF04] hover:bg-[#ffdb2e] text-[#1a0a0a] font-semibold flex-1">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />} Reserve now · Pay ₹{priceInfo?.total} at venue
                  </Button>
                )}
              </div>
              {!cfg.razorpay_enabled && <p className="text-xs text-slate-500 mt-2">Online payment (Razorpay) activates once API keys are added in settings.</p>}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
